"use client";

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Loader2, Send, Paperclip } from "lucide-react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { makeRequest } from "@/utils/makeRequest";
import { toast } from "sonner";
import { getErrorMessage } from "@/utils/getErrorMessage";
import { uploadFiles } from "@/upload/upload";
import { queryClient } from "@/services/queryClient";
import { useAppSelector } from "@/store/hooks";

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated?: () => void;
};

export default function NewTicketModal({ open, onClose, onCreated }: Props) {
  const { user } = useAppSelector((state) => state.auth);

  const userId = user?.id || "";

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [form, setForm] = useState({
    subject: "",
    category: "ACCOUNT",
    priority: "MEDIUM",
    message: "",
    images: [] as string[],
  });

  const handleUpload = async (files: FileList | null) => {
    if (!files) return;

    setUploading(true);

    try {
      const results = await uploadFiles({
        files: Array.from(files),
        folder: "/support/tickets",
        fileNamePrefix: "ticket",
      });

      const urls = results.map((r) => r.url);

      setForm((prev) => ({
        ...prev,
        images: [...prev.images, ...urls],
      }));

      toast.success("Upload successful");
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.subject || !form.message) return;

    setLoading(true);

    const data = {
      subject: form.subject,
      category: form.category,
      priority: form.priority,
      // The initial message will be handled in the service as first reply
      message: form.message,
      images: form.images,
    };

    try {
      const res = await makeRequest.post("/ticket", data);

      if (res) {
        setForm({
          subject: "",
          category: "ACCOUNT",
          priority: "MEDIUM",
          message: "",
          images: [],
        });
        onCreated?.();
        onClose();

        queryClient.invalidateQueries({ queryKey: ["tickets", userId] });
      }
    } catch (error) {
      console.error(error);
      toast.error(getErrorMessage(error) || "Failed to create ticket");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create Support Ticket</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <Input
            className="h-11"
            placeholder="Ticket subject (e.g. Withdrawal issue)"
            value={form.subject}
            onChange={(e) => setForm({ ...form, subject: e.target.value })}
          />

          <Select
            value={form.category}
            onValueChange={(val) => setForm({ ...form, category: val })}
          >
            <SelectTrigger className="w-full h-11">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="WITHDRAWAL">Withdrawal</SelectItem>
              <SelectItem value="DEPOSIT">Deposit</SelectItem>
              <SelectItem value="KYC">KYC</SelectItem>
              <SelectItem value="ACCOUNT">Account</SelectItem>
              <SelectItem value="FEES">Fees</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={form.priority}
            onValueChange={(val) => setForm({ ...form, priority: val })}
          >
            <SelectTrigger className="w-full h-11">
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="LOW">Low</SelectItem>
              <SelectItem value="MEDIUM">Medium</SelectItem>
              <SelectItem value="HIGH">High</SelectItem>
              <SelectItem value="URGENT">Urgent</SelectItem>
            </SelectContent>
          </Select>

          <Textarea
            placeholder="Describe your issue in detail..."
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            rows={5}
          />

          <div className="flex items-center gap-2">
            <label className="cursor-pointer flex items-center gap-2 text-sm text-gray-500 hover:text-foreground">
              <Paperclip className="w-4 h-4" />
              Attach files
              <input
                type="file"
                multiple
                hidden
                onChange={(e) => handleUpload(e.target.files)}
              />
            </label>
          </div>

          {/* {form.images.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {form.images.map((img, i) => (
                <img
                  key={i}
                  src={img}
                  className="w-16 h-16 rounded object-cover border"
                  alt="attachment"
                />
              ))}
            </div>
          )} */}

          {form.images.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              {form.images.map((img, i) => (
                <div key={i} className="relative">
                  <img
                    src={img}
                    className="w-16 h-16 rounded object-cover border"
                    alt="attachment"
                  />
                  <button
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        images: prev.images.filter((_, idx) => idx !== i),
                      }))
                    }
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 text-xs"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={loading || uploading || !form.subject || !form.message}
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              {uploading ? "Uploading image..." : "Submit Ticket"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
